import { defineStore } from 'pinia';
import { markRaw } from 'vue';
import { registerOnMap, removeFromMap, setVisibilityOnMap, setOpacityOnMap, setSourceDataOnMap, clearSourceDataOnMap, listRuntimeByCategory, detachMap, } from '@/composables/gtfs/map/mapLayerRegistry.js';
import { EMPTY_FC } from '@/composables/gtfs/map/gtfsMapLayers.js';
import { resetOwnerLayerCache } from '@/composables/gtfs/map/ownerLayerCache.js';
function keyOf(category, ownerId, layerId) {
    return `${category}::${ownerId}::${layerId}`;
}
/**
 * Merkezi katman yöneticisi.
 * Kategori davranışı:
 * - base: analiz değişiminde ASLA temizlenmez; kapatılamaz (opacity ayarlanır)
 * - analysis: analiz değişince tamamen temizlenir
 * - reference: analiz değişince silinmez; görünürlük kullanıcı toggle'ına bağlı
 * - interaction: analiz değişiminde ve etkileşim bitiminde temizlenir
 */
export const useMapLayerStore = defineStore('mapLayer', {
    state: () => ({
        /** @type {Record<string, MapLayerRecord>} */
        layers: {},
    }),
    getters: {
        byCategory: (state) => (category) => Object.values(state.layers).filter((l) => l.category === category),
        byOwner: (state) => (ownerId) => Object.values(state.layers).filter((l) => l.ownerId === ownerId),
        getLayer: (state) => (category, ownerId, layerId) => state.layers[keyOf(category, ownerId, layerId)] || null,
    },
    actions: {
        /**
         * @param {LayerCategory} category
         * @param {string} ownerId
         * @param {string} layerId
         * @param {object} layerInstance — MapLibre descriptor (markRaw)
         * @param {object} [opts]
         */
        registerLayer(category, ownerId, layerId, layerInstance, opts = {}) {
            const key = keyOf(category, ownerId, layerId);
            const existing = this.layers[key];
            const locked = category === 'base' ? true : !!opts.locked;
            const visible = opts.visible !== undefined
                ? !!opts.visible
                : existing
                    ? existing.visible
                    : true;
            const userVisible = existing?.userVisible !== undefined
                ? existing.userVisible
                : visible;
            const instance = markRaw(layerInstance || {});
            registerOnMap(category, ownerId, layerId, instance);
            const mapLayerIds = instance.layers?.map((l) => l.id) ||
                instance.layerIds ||
                existing?.mapLayerIds ||
                [];
            const sourceId = instance.sourceId ||
                existing?.sourceId ||
                layerId;
            this.layers[key] = {
                category,
                ownerId,
                layerId,
                mapLayerIds: [...mapLayerIds],
                sourceId,
                visible: locked ? true : visible,
                userVisible: locked ? true : userVisible,
                opacity: opts.opacity ?? existing?.opacity ?? 1,
                opacityPaintProp: opts.opacityPaintProp ||
                    existing?.opacityPaintProp ||
                    'fill-opacity',
                locked,
                label: opts.label || existing?.label || layerId,
                meta: opts.meta || existing?.meta || {},
            };
            this._syncVisibility(this.layers[key]);
            return this.layers[key];
        },
        removeLayer(category, ownerId, layerId) {
            const key = keyOf(category, ownerId, layerId);
            const rec = this.layers[key];
            if (!rec)
                return;
            if (rec.sourceId)
                clearSourceDataOnMap(rec.sourceId);
            removeFromMap(category, ownerId, layerId);
            delete this.layers[key];
        },
        /**
         * Kategori temizliği. Base çağrılırsa no-op (güvenlik).
         * clearAnalysisLayers = clearCategory('analysis') alias — ayrı mekanizma YOK.
         */
        clearCategory(category) {
            if (category === 'base')
                return;
            const targets = Object.values(this.layers).filter((l) => !!l && l.category === category);
            for (const rec of targets) {
                this.removeLayer(rec.category, rec.ownerId, rec.layerId);
            }
            // Runtime orphan cleanup
            for (const entry of listRuntimeByCategory(category)) {
                removeFromMap(entry.category, entry.ownerId, entry.layerId);
            }
            if (category === 'analysis') {
                resetOwnerLayerCache();
            }
        },
        /** clearCategory('analysis') kısayolu — AYNI fonksiyon */
        clearAnalysisLayers() {
            this.clearCategory('analysis');
        },
        clearInteractionLayers() {
            this.clearCategory('interaction');
        },
        /**
         * Analiz geçişi: Analysis + Interaction temizle.
         * Base ve Reference korunur.
         */
        onAnalysisChange() {
            this.clearCategory('analysis');
            this.clearCategory('interaction');
        },
        /** Harita teardown — her şey */
        clearAll() {
            const keys = Object.keys(this.layers);
            for (const key of keys) {
                const rec = this.layers[key];
                if (rec.sourceId)
                    clearSourceDataOnMap(rec.sourceId);
                removeFromMap(rec.category, rec.ownerId, rec.layerId);
                delete this.layers[key];
            }
            detachMap();
        },
        toggleVisibility(category, ownerId, layerId) {
            const rec = this.layers[keyOf(category, ownerId, layerId)];
            if (!rec)
                return;
            rec.visible = !rec.visible;
            rec.userVisible = rec.visible;
            this._syncVisibility(rec);
        },
        setVisibility(category, ownerId, layerId, visible, opts = {}) {
            const rec = this.layers[keyOf(category, ownerId, layerId)];
            if (!rec)
                return;
            rec.visible = !!visible;
            if (!opts.silent) {
                rec.userVisible = rec.visible;
            }
            this._syncVisibility(rec);
        },
        setOpacity(category, ownerId, layerId, opacity) {
            const rec = this.layers[keyOf(category, ownerId, layerId)];
            if (!rec)
                return;
            rec.opacity = Math.max(0, Math.min(1, Number(opacity) || 0));
            setOpacityOnMap(rec.mapLayerIds, rec.opacityPaintProp, rec.opacity);
        },
        setSourceData(category, ownerId, layerId, data) {
            const rec = this.layers[keyOf(category, ownerId, layerId)];
            if (!rec?.sourceId)
                return;
            const fc = data || EMPTY_FC;
            setSourceDataOnMap(rec.sourceId, fc || EMPTY_FC);
            const n = Array.isArray(fc?.features) ? fc.features.length : 0;
            rec.meta = { ...(rec.meta || {}), hasData: n > 0, featureCount: n };
        },
        setSourceDataBySourceId(sourceId, data) {
            setSourceDataOnMap(sourceId, data || EMPTY_FC);
        },
        _syncVisibility(rec) {
            // locked = analiz geçişinde silinmez; görünürlük kullanıcı toggle'ına uyar
            setVisibilityOnMap(rec.mapLayerIds, !!rec.visible);
        },
    },
});
